import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router'
import { useClient } from 'urql'
import {
  GetShippingTestDocument,
  GetShippingTestQuery,
  useGetTagSubscription,
} from '../../generated/urql.anonymous'
import {
  GetProfilesSubscription,
  useDeleteClientFavouriteTestMutation,
} from '../../generated/urql.client'
import {
  laboratoryTestsToPriceString,
  laboratoryTestsToWaitingTimesList,
} from '../../lib/test'
import { AuthContext } from '../../provider/auth'
import {
  BasketContext,
  isShippingTestBasketItem,
  isTestBasketItem,
} from '../../provider/basket'
import { useI18n } from '../../provider/i18n'
import { Link, useLocation } from 'react-router-dom'
import { Disclosure, Transition } from '@headlessui/react'
import {
  MdAlarmOn,
  MdKeyboardArrowRight,
  MdShoppingBasket,
} from 'react-icons/md'
import { Blue600 } from '../../lib/colors'
import Markdown from 'react-markdown'
import ProfileSelect from '../../components/ProfileSelect'
import { MessageBox } from '../../components/MessageBox'
import {
  ConfirmDialog,
  ConfirmDialogType,
} from '../../components/ConfirmDialog'
import { useAtomValue } from 'jotai'
import { profilesSubscriptionAtom } from '../../store/subscription.atoms'

const ShippingTest = () => {
  const params = useParams<{ id: string }>()
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const basket = useContext(BasketContext)
  const client = useClient()
  const [generalError, setGeneralError] = useState<string>()
  const [shippingTest, setShippingTest] = useState<GetShippingTestQuery>()
  const [, deleteClientFavouriteTest] = useDeleteClientFavouriteTestMutation()
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const id = parseInt(params.id || '0', 10)
  const location = useLocation()
  const confirmDialogRef = useRef<ConfirmDialogType>(null)

  const test = !!shippingTest?.test?.length ? shippingTest.test[0] : undefined
  const profiles = profilesData?.client_profile
  const [selectedProfile, setSelectedProfile] =
    useState<GetProfilesSubscription['client_profile'][number]>()
  const isAddedToBasket = useMemo(
    () =>
      auth.isLogged
        ? basket.items
            .filter(isShippingTestBasketItem)
            .filter(({ test: { id } }) => test?.id === id).length >=
          (profiles?.length || 0)
        : !!basket.items.find(
            (item) => isShippingTestBasketItem(item) && item.test?.id === id
          ),
    [auth.isLogged, basket.items, profiles, test?.id]
  )

  useEffect(() => {
    !selectedProfile &&
      profiles &&
      setSelectedProfile(profiles.find(({ isMain }) => isMain) || profiles[0])
  }, [profiles])

  const [{ data: tagData }] = useGetTagSubscription({
    pause: !location.state?.tagId,
    variables: {
      id: location.state?.tagId || '',
    },
  })

  const tag = tagData?.tag_by_pk

  const fetchShippingTest = useCallback(() => {
    client
      .query(
        GetShippingTestDocument,
        {
          id,
        },
        { requestPolicy: 'network-only' }
      )
      .toPromise()
      .then(({ data }) => setShippingTest(data))
      .catch(console.error)
  }, [client, id])

  useEffect(fetchShippingTest, [fetchShippingTest])

  const doAddToBasket = useCallback(() => {
    if (isAddedToBasket || !test) {
      return
    }

    const profilesCount = profilesData?.client_profile.length || 0

    if (profilesCount === 0) {
      basket.addItem({ test, shouldShipToClient: true })
    } else if (profilesCount === 1) {
      basket.addItem({
        test,
        shouldShipToClient: true,
        profile: profilesData?.client_profile[0],
      })
    } else {
      basket.addItem({
        test,
        shouldShipToClient: true,
        profile: selectedProfile,
      })
    }
  }, [
    basket,
    isAddedToBasket,
    profilesData?.client_profile,
    selectedProfile,
    test,
  ])

  const doRemoveFromBasket = useCallback(() => {
    if (isAddedToBasket && test) {
      const items = basket.items
        .filter(isShippingTestBasketItem)
        .filter((item) => item.test.id === test.id)
      if (items.length === 1) {
        basket.removeItem(items[0])
      } else if (items.length > 1) {
        confirmDialogRef.current?.show({
          message: i18n.t(
            'screens.catalog.test.basket.removeForAllProfiles.title'
          ),
          onSuccess: () => {
            basket.removeItem(...items)
          },
        })
      }
    }
  }, [basket, i18n, isAddedToBasket, test])

  return (
    <div className="mb-10">
      <div className="text-blue-700 mb-5">
        <Link to="/shipping-tests">
          {i18n.t('screens.catalog.index.shippingTests.header')}
        </Link>
        {' > '}
        {tag && (
          <>
            <Link
              to={`/shipping-tests/tag/${encodeURIComponent(tag.id)}`}
            >
              {tag.tag}
            </Link>
            {' > '}
          </>
        )}
        {test?.name || test?.shortName || '-'}
      </div>

      {generalError && <MessageBox>{generalError}</MessageBox>}

      {test && (
        <>
          <div className="flex justify-between items-center mb-8 mt-5">
            <h1 className="text-2xl md:text-3xl font-bold leading-tight">
              {test.name || test.shortName}
            </h1>
          </div>
          <div className="hl-panel">
            {!!test.description && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="p-4 md:p-6 text-lg font-bold flex justify-between w-full border-b border-b-border border-opacity-20">
                      <>
                        {i18n.t('screens.catalog.test.description.label')}
                        <MdKeyboardArrowRight
                          className={open ? 'rotate-90 transform' : ''}
                          size={24}
                          color={Blue600}
                        />
                      </>
                    </Disclosure.Button>
                    <Transition
                      className="overflow-hidden"
                      enter="transition-all ease-in-out duration-[500ms]"
                      enterFrom="transform max-h-0"
                      enterTo="transform max-h-[1000px]"
                      leave="transition-all ease-in-out duration-[500ms]"
                      leaveFrom="transform max-h-[1000px]"
                      leaveTo="transform max-h-0"
                    >
                      <Disclosure.Panel className="p-4 md:p-6 border-b border-b-border border-opacity-20">
                        <Markdown className="markdown">
                          {test.description}
                        </Markdown>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            )}
            {!!test.preparation && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="p-4 md:p-6 text-lg font-bold flex justify-between w-full border-b border-b-border border-opacity-20">
                      <>
                        {i18n.t('screens.catalog.test.preparation.label')}
                        <MdKeyboardArrowRight
                          className={open ? 'rotate-90 transform' : ''}
                          size={24}
                          color={Blue600}
                        />
                      </>
                    </Disclosure.Button>
                    <Transition
                      className="overflow-hidden"
                      enter="transition-all ease-in-out duration-[500ms]"
                      enterFrom="transform  max-h-0"
                      enterTo="transform  max-h-[1000px]"
                      leave="transition-all ease-in-out duration-[500ms]"
                      leaveFrom="transform  max-h-[1000px]"
                      leaveTo="transform  max-h-0"
                    >
                      <Disclosure.Panel className="p-4 md:p-6 border-b border-b-border border-opacity-20">
                        <Markdown>{test.preparation}</Markdown>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            )}
            <div className="bg-blue-200 rounded-b-2xl md:flex justify-between items-center p-5 md:py-12 md:px-8 gap-2.5 md:gap-5 grid grid-cols-2">
              {!!test.laboratoryTests && (
                <>
                  <div className="flex gap-2.5 items-center md:mr-auto justify-center md:justify-start">
                    <MdAlarmOn size={24} color={Blue600} />
                    <p>
                      {i18n.t('screens.catalog.test.waitingTime.label')}:{' '}
                      <strong>
                        {laboratoryTestsToWaitingTimesList(
                          test.laboratoryTests
                        ).join(', ')}
                      </strong>
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-center justify-center md:justify-start">
                    <MdShoppingBasket size={24} color={Blue600} />
                    <p className="text-md md:text-lg font-bold">
                      {i18n.t('screens.catalog.test.price', {
                        price: laboratoryTestsToPriceString(
                          test.laboratoryTests
                        ),
                      })}
                    </p>
                  </div>
                </>
              )}
              <button
                className={`hl-button ${
                  isAddedToBasket ? 'hl-gradient-red' : ''
                }`}
                onClick={!isAddedToBasket ? doAddToBasket : doRemoveFromBasket}
              >
                {!isAddedToBasket
                  ? i18n.t('screens.catalog.test.basket.add')
                  : i18n.t('screens.catalog.test.basket.remove')}
              </button>
              {auth.accessToken && !isAddedToBasket && (
                <ProfileSelect
                  onChange={setSelectedProfile}
                  value={selectedProfile}
                  filter={(profile) =>
                    !basket.items.find(
                      (item) =>
                        isTestBasketItem(item) &&
                        item.test.id === test.id &&
                        item.profile?.id === profile.id
                    )
                  }
                />
              )}
            </div>
          </div>
        </>
      )}
      <ConfirmDialog ref={confirmDialogRef} />
    </div>
  )
}

export default ShippingTest
